import { Button, Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import { resolveReceiptFields, type ReceiptFieldFlags } from '@mlm/shared';
import type { PosReceiptItem, ReceiptHeader } from '../lib/api';

/**
 * BF3 — normalized receipt shape rendered by both the checkout flow and the
 * releasing queue (release + reprint). The `.receipt-tape` element is the only
 * thing that survives `@media print` (see index.css), sized for ~80mm thermal.
 */
export type ReceiptData = {
  orderCode: string;
  status: string;
  /** Sold lines. Always printed — a ₱0 item still gets its name on the tape. */
  items: PosReceiptItem[];
  subtotal: string;
  discountTotal: string;
  total: string;
  payment: { method: string; amountTendered: string; changeDue: string } | null;
  kitCodes: Array<{ code: string; pin: string }>;
  header: ReceiptHeader | null;
  /** Ad-hoc charge added on top of the goods. Printed under its own label. */
  customFeeLabel?: string | null;
  customFeeAmount?: string | null;
  /** Customer the sale was rung up for. */
  memberName?: string | null;
  /** Till operator who processed it. */
  cashierName?: string | null;
  /** Branch / stockist name. Distinct from the order-code prefix. */
  branchName?: string | null;
  /** ISO timestamp of the sale, printed as separate date + time rows. */
  createdAt?: string | null;
  /** POS remarks. Off by default — internal memo, not customer-facing. */
  remarks?: string | null;
  /**
   * Per-branch visibility flags (`branches.receipt_fields`), sparse. Undefined
   * or missing keys resolve to the shared defaults, so an unconfigured branch
   * prints everything rather than a blank tape.
   */
  fields?: Partial<ReceiptFieldFlags>;
};

/**
 * Date and time as two rows rather than one locale string: at 80mm a combined
 * stamp wraps unpredictably, and the two are read for different reasons (which
 * day for a return window, which minute for a drawer dispute).
 */
function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}
function fmtTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * A missing/garbled amount prints `—`, never `₱0.00`. The old formatter
 * coerced null/''/undefined straight to `₱0.00`, which is indistinguishable
 * from a genuine zero-value sale — a cashier escalates a dash, not a zero.
 */
function fmt(v: string | null | undefined): string {
  const n = Number(v ?? NaN);
  return Number.isFinite(n) ? n.toFixed(2) : '—';
}
const peso = (v: string | null | undefined) => `₱${fmt(v)}`;

export function Receipt({
  data,
  onNew,
  onClose,
  newLabel = 'New sale',
}: {
  data: ReceiptData;
  onNew?: () => void;
  onClose?: () => void;
  newLabel?: string;
}) {
  const queued = data.status === 'to_release';
  const fields = resolveReceiptFields(data.fields);

  return (
    <Card className="max-w-md mx-auto">
      <CardHeader className="no-print">
        <CardTitle>{queued ? 'Payment received' : 'Sale complete'}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="receipt-tape font-mono text-sm space-y-1">
          {data.header && (
            <div className="text-center space-y-0.5 pb-2 border-b border-dashed">
              {data.header.companyName && (
                <div className="font-semibold text-base">{data.header.companyName}</div>
              )}
              {data.header.address && <div className="text-xs">{data.header.address}</div>}
              {data.header.phone && <div className="text-xs">{data.header.phone}</div>}
              {data.header.tin && <div className="text-xs">TIN: {data.header.tin}</div>}
            </div>
          )}

          {/* Transaction meta. The order code is unconditional — it's the one
              thing a customer quotes back — everything else is per-branch
              configurable in Admin → Branch → POS & Receipt. */}
          <div className="flex justify-between pt-1">
            <span>Order</span>
            <span>{data.orderCode}</span>
          </div>
          {fields.showBranchName && data.branchName && (
            <div className="flex justify-between">
              <span>Branch</span>
              <span className="text-right break-words">{data.branchName}</span>
            </div>
          )}
          {fields.showDateTime && data.createdAt && (
            <>
              <div className="flex justify-between">
                <span>Date</span>
                <span>{fmtDate(data.createdAt)}</span>
              </div>
              <div className="flex justify-between">
                <span>Time</span>
                <span>{fmtTime(data.createdAt)}</span>
              </div>
            </>
          )}
          {fields.showMemberName && data.memberName && (
            <div className="flex justify-between">
              <span>Customer</span>
              <span className="text-right break-words">{data.memberName}</span>
            </div>
          )}
          {fields.showCashierName && data.cashierName && (
            <div className="flex justify-between">
              <span>Cashier</span>
              <span className="text-right break-words">{data.cashierName}</span>
            </div>
          )}

          {/* Sold lines. No <table> — at 80mm a table's column widths collapse
              unpredictably, so each line is a wrapping name followed by a
              qty × price / line-total row. */}
          <div className="border-t border-dashed pt-1 mt-1 space-y-1">
            {data.items.length === 0 ? (
              <div className="text-[10px] text-muted-foreground text-center">
                (no line items recorded)
              </div>
            ) : (
              data.items.map((it, i) => (
                <div key={`${it.name}-${it.variantName ?? ''}-${i}`}>
                  <div className="break-words leading-tight">{it.name}</div>
                  {it.variantName && (
                    <div className="pl-2 text-[10px] text-muted-foreground leading-tight">
                      {it.variantName}
                    </div>
                  )}
                  <div className="flex justify-between pl-2">
                    <span>
                      {it.qty} × {fmt(it.unitPrice)}
                    </span>
                    <span className="tabular-nums">
                      {Number(it.lineTotal) === 0 ? 'FREE' : fmt(it.lineTotal)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="flex justify-between border-t border-dashed pt-1">
            <span>Subtotal</span>
            <span>{peso(data.subtotal)}</span>
          </div>
          {Number(data.discountTotal) > 0 && (
            <div className="flex justify-between">
              <span>Discount</span>
              <span>−{peso(data.discountTotal)}</span>
            </div>
          )}
          {/* Ad-hoc charge. Always printed when charged — a customer must be
              able to see what the extra money was for. Not flag-gated. */}
          {Number(data.customFeeAmount) > 0 && (
            <div className="flex justify-between">
              <span className="break-words">{data.customFeeLabel || 'Fee'}</span>
              <span>{peso(data.customFeeAmount)}</span>
            </div>
          )}
          {/* A genuinely ₱0 sale (e.g. a ₱0-priced product) is legitimate but
              looks identical to a broken tape — say so on the paper. */}
          {data.items.length > 0 && Number(data.total) === 0 && (
            <div className="text-center text-[10px] font-semibold pt-1">
              ** ZERO-VALUE SALE **
            </div>
          )}
          <div className="flex justify-between font-semibold border-t border-dashed pt-1">
            <span>Total</span>
            <span>{peso(data.total)}</span>
          </div>

          {data.payment && (
            <>
              <div className="flex justify-between pt-1">
                <span className="capitalize">{data.payment.method}</span>
                <span>{peso(data.payment.amountTendered)}</span>
              </div>
              {Number(data.payment.changeDue) > 0 && (
                <div className="flex justify-between font-semibold">
                  <span>Change</span>
                  <span>{peso(data.payment.changeDue)}</span>
                </div>
              )}
            </>
          )}

          {data.kitCodes.length > 0 && (
            <div className="border-t border-dashed pt-2 mt-1 space-y-1">
              <div className="text-center text-xs font-semibold">ACTIVATION CODES</div>
              {data.kitCodes.map((c) => (
                <div key={c.code} className="flex justify-between">
                  <span className="tracking-widest">{c.code}</span>
                  <span>PIN {c.pin}</span>
                </div>
              ))}
              <div className="text-center text-[10px] text-muted-foreground">
                Keep this slip — codes activate your slot.
              </div>
            </div>
          )}

          {queued && (
            <div className="border-t border-dashed pt-2 mt-1 text-center text-xs">
              ★ Claim your items at the Releasing area ★
            </div>
          )}

          {fields.showRemarks && data.remarks && (
            <div className="border-t border-dashed pt-2 mt-1 text-[10px] break-words">
              <span className="text-muted-foreground">Remarks: </span>
              {data.remarks}
            </div>
          )}

          {data.header?.footer && (
            <div className="text-center text-[10px] text-muted-foreground pt-2">
              {data.header.footer}
            </div>
          )}
        </div>

        <div className="no-print flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => window.print()}>
            Print
          </Button>
          {onNew && (
            <Button className="flex-1" onClick={onNew}>
              {newLabel}
            </Button>
          )}
          {onClose && (
            <Button variant="outline" className="flex-1" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
